import "./style.css";
// Faviconet settes fra kit-en framfor å kopieres inn i public/. Da kan det
// ikke drifte fra merkevaren, som regel 1 krever - prisen er at det settes
// først når JS har kjørt, noe som er greit for en SPA.
import faviconUrl from "@actiwise/brand/assets/symbol-180.png";

const favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
if (favicon) favicon.href = faviconUrl;
import {
  fornyMedRefreshToken,
  gjeldendeBruker,
  kreverSamhandling,
  nullstillStilleInnlogging,
  stilleInnlogging,
  stilleInnloggingForsokt,
  userManager,
} from "./auth";
import { settSesjonsStatus, visFeil, visInnlogging, visLaster, visPortal } from "./ui";
import { STATUS_STI, visStatusside } from "./status";

const CALLBACK_STI = "/auth/callback";

/**
 * Etter innlogging sender Keycloak brukeren tilbake til /auth/callback med
 * ?code=…&state=…. Vi bytter koden mot tokens, og rydder deretter URL-en med
 * replaceState slik at koden ikke blir liggende i historikken eller i en
 * lenke brukeren tilfeldigvis deler.
 */
async function haandterCallback(): Promise<void> {
  try {
    const bruker = await userManager().signinRedirectCallback();
    window.history.replaceState({}, document.title, "/");
    // Neste gang fanen står uten bruker, er det verdt et nytt stille forsøk.
    nullstillStilleInnlogging();
    visPortal(bruker);
  } catch (feil) {
    window.history.replaceState({}, document.title, "/");
    // Svaret på et stille forsøk når det ikke finnes noen SSO-sesjon.
    // Helt normalt for den som ikke er innlogget - vis knappen.
    if (kreverSamhandling(feil)) {
      visInnlogging();
      return;
    }
    console.error("Innloggingen kunne ikke fullføres:", feil);
    visFeil(
      "Innloggingen ble ikke fullført",
      "Lenken kan ha vært brukt før, eller utløpt. Prøv å logge inn på nytt.",
    );
  }
}

function lyttPaaSesjon(): void {
  userManager().events.addAccessTokenExpiring(() => {
    settSesjonsStatus({ type: "fornyer" });
  });

  userManager().events.addUserLoaded((bruker) => {
    settSesjonsStatus({ type: "aktiv", fornyetKl: new Date() });
    visPortal(bruker);
  });

  // Stille fornyelse feilet. Vanligste årsak er at SSO-sesjonen er over -
  // enten 8-timersgrensen (ssoSessionMaxLifespan) eller en time uten
  // aktivitet (ssoSessionIdleTimeout). Da må brukeren logge inn på nytt.
  userManager().events.addSilentRenewError((feil) => {
    console.warn("Stille fornyelse feilet:", feil);
    settSesjonsStatus({ type: "utlopt" });
  });

  userManager().events.addUserSignedOut(() => {
    settSesjonsStatus({ type: "utlopt" });
  });
}

async function start(): Promise<void> {
  // Vises mens vi venter på callback-utvekslingen eller sesjonsoppslaget.
  // Uten den står siden tom i det som kan bli et par sekunder.
  visLaster();

  if (window.location.pathname === CALLBACK_STI) {
    lyttPaaSesjon();
    await haandterCallback();
    return;
  }

  lyttPaaSesjon();

  const bruker = await gjeldendeBruker();
  if (bruker && !bruker.expired) {
    visPortal(bruker);
    return;
  }

  if (bruker) {
    try {
      const fornyet = await fornyMedRefreshToken(bruker);
      if (fornyet) {
        visPortal(fornyet);
        return;
      }
    } catch (feil) {
      console.warn("Kunne ikke fornye utløpt sesjon:", feil);
    }
  }

  if (!stilleInnloggingForsokt()) {
    try {
      // Navigerer bort. Svaret kommer til /auth/callback.
      await stilleInnlogging();
      return;
    } catch (feil) {
      // Typisk at discovery-dokumentet ikke kunne hentes - IdP-en er nede.
      // Da er innloggingsbildet med lenke til driftsstatus det beste vi har.
      console.warn("Stille innlogging kunne ikke starte:", feil);
    }
  }

  visInnlogging();
}

// Driftsstatus tegnes uten innlogging, og før noe som rører IdP-en: ingen
// UserManager, ingen discovery, ingen stille fornyelse. Siden trengs mest når
// id.actiwise.no er nede. userManager() er lat av samme grunn - se auth.ts.
if (window.location.pathname.replace(/\/+$/, "") === STATUS_STI) {
  visStatusside();
} else {
  start().catch((feil: unknown) => {
    console.error("Launcheren kunne ikke starte:", feil);
    visFeil(
      "Noe gikk galt",
      feil instanceof Error ? feil.message : "Ukjent feil ved oppstart.",
    );
  });
}
